import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { ArrowsClockwise, Eye, DownloadSimple, CircleNotch, Clock, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { StatusBadge, CountLinesTable, CATEGORY_LABEL, STATUS, fmt } from "./InventoryAdjustmentRequests";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// What each trail entry means in plain words - the actions written by
// physical_inventory_service.log_audit.
const TRAIL = {
  submitted: { label: "Counted and submitted", className: "bg-[#EFF8FF] border-[#B2DDFF]" },
  approved: { label: "Approved and sent to SAP", className: "bg-[#ECFDF3] border-[#ABEFC6]" },
  rejected: { label: "Rejected", className: "bg-[#FEF3F2] border-[#FECDCA]" },
  stock_changed: { label: "Blocked - SAP stock had moved since the count", className: "bg-[#FFFAEB] border-[#FEDF89]" },
  verified_no_adjustment: { label: "Verified - counted, no difference to post", className: "bg-[#F9FAFB] border-[#E4E7EC]" },
  sap_completed: { label: "Posted in SAP", className: "bg-[#ECFDF3] border-[#ABEFC6]" },
  sap_partially_posted: { label: "Partially posted in SAP", className: "bg-[#FFFAEB] border-[#FEDF89]" },
  sap_failed: { label: "SAP rejected the posting", className: "bg-[#FEF3F2] border-[#FECDCA]" },
};

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const docsOf = (r) => (r.postings || []).filter((p) => p.sap_gac_id).map((p) => p.sap_gac_id);

const trailDetail = (entry) => {
  const d = entry.detail || {};
  if (entry.action === "rejected") return d.reason || "";
  if (entry.action === "stock_changed") {
    return (d.changes || []).map((c) => `${c.product_id} @ ${c.warehouse_id}: counted against ${fmt(c.counted_against)}, SAP had ${fmt(c.current_qty)}`).join(" · ");
  }
  if (entry.action === "approved") {
    return (d.groups || []).map((g) => `${g.group === "issue" ? "Goods Issue" : "Goods Return"} ${fmt(g.quantity)}`).join(" · ")
      + (d.cost_center_id ? ` → cost centre ${d.cost_center_id}` : "");
  }
  if (entry.action === "submitted") {
    const s = d.summary || {};
    return `${s.total_items ?? 0} item(s) · ${s.shortage_items ?? 0} shortage · ${s.excess_items ?? 0} excess`;
  }
  if (entry.action === "sap_failed") return d.error || "";
  if (String(entry.action).startsWith("sap_")) {
    return (d.documents || []).map((x) => `${x.group === "issue" ? "Goods Issue" : "Goods Return"} ${x.sap_gac_id || x.status}`).join(" · ");
  }
  return "";
};

const CountTrail = ({ trail, loading }) => {
  if (loading) return <p className="text-xs text-[#667085]" data-testid="pi-audit-trail-loading">Loading the trail...</p>;
  if (!trail.length) return <p className="text-xs text-[#98A2B3]">No trail entries recorded.</p>;
  return (
    <div className="space-y-1.5" data-testid="pi-audit-trail">
      {trail.map((e, i) => {
        const meta = TRAIL[e.action] || { label: e.action, className: "bg-[#F9FAFB] border-[#E4E7EC]" };
        const extra = trailDetail(e);
        return (
          <div key={i} className={`border rounded-lg px-3 py-2 ${meta.className}`} data-testid={`pi-audit-trail-${e.action}`}>
            <p className="text-xs font-bold text-[#101828]">{meta.label}</p>
            <p className="text-[11px] text-[#667085]">{e.actor} · {when(e.at)}</p>
            {extra && <p className="text-[11px] text-[#475467] mt-0.5 break-words">{extra}</p>}
          </div>
        );
      })}
    </div>
  );
};

export const PhysicalInventoryAudit = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState("all");
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);
  const [trail, setTrail] = useState([]);
  const [trailLoading, setTrailLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/physical-inventory/requests`, { params: { scope: "all", limit: 500, include_removed: true } });
      setRequests(data.requests || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the Physical Inventory audit");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sites = useMemo(() => [...new Set(requests.map((r) => r.site_id))].sort(), [requests]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return requests.filter((r) => {
      if (site !== "all" && r.site_id !== site) return false;
      if (status !== "all" && r.status !== status) return false;
      if (!q) return true;
      return r.request_id.toLowerCase().includes(q)
        || (r.requester_name || "").toLowerCase().includes(q)
        || docsOf(r).some((d) => String(d).includes(q));
    });
  }, [requests, site, status, query]);

  const openDetail = async (requestId) => {
    setTrail([]);
    setTrailLoading(true);
    try {
      const [{ data: doc }, { data: audit }] = await Promise.all([
        axios.get(`${API}/physical-inventory/requests/${requestId}`),
        axios.get(`${API}/physical-inventory/audit`, { params: { request_id: requestId } }),
      ]);
      setDetail(doc);
      setTrail([...(audit.audit || [])].reverse());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open that count");
    } finally {
      setTrailLoading(false);
    }
  };

  const stillPosting = useMemo(() => requests.filter((r) => r.status === "processing"), [requests]);

  // A count only sits at "Posting to SAP..." because the SAP answer never came
  // back. This asks SAP about every one of them in a single pass and settles
  // each - no opening them one by one (user, Oct 24 2026).
  const checkWithSap = async () => {
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/physical-inventory/recover-pending`);
      setCheckResult(data);
      if (data.recovered.length) {
        toast.success(`${data.recovered.length} movement(s) found in SAP: ${data.recovered.map((r) => r.sap_gac_id).join(", ")}`);
      } else if (data.not_in_sap.length) {
        toast.warning(`${data.not_in_sap.length} movement(s) never reached SAP - they can be counted again`);
      } else if (data.error) {
        toast.error(data.error);
      } else {
        toast.success("Every movement is already settled with SAP");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "SAP could not be reached for this check");
    } finally {
      setChecking(false);
    }
  };

  const exportCsv = () => {    const header = ["Request ID", "Counted on", "Site", "Type", "Requested by", "Status", "Items", "Shortage items",
      "Excess items", "Issue qty", "Return qty", "SAP documents", "Decided by", "Decided on", "Remarks", "Rejection reason"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = rows.map((r) => [
      r.request_id, when(r.created_at), r.site_id, CATEGORY_LABEL[r.category] || r.category, r.requester_name,
      STATUS[r.status]?.label || r.status, r.summary?.total_items ?? 0, r.summary?.shortage_items ?? 0,
      r.summary?.excess_items ?? 0, r.summary?.total_issue_qty ?? 0, r.summary?.total_return_qty ?? 0,
      docsOf(r).join(" / "), r.approved_by || "", when(r.approved_at), r.remarks || "", r.rejection_reason || "",
    ].map(escape).join(","));
    const url = URL.createObjectURL(new Blob([[header.map(escape).join(","), ...body].join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `physical-inventory-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} count(s) exported`);
  };

  return (
    <div className="space-y-4" data-testid="physical-inventory-audit">
      <p className="text-xs text-[#667085] max-w-3xl">
        Every physical count ever submitted, kept permanently - including counts that matched and were never adjusted,
        and counts that were rejected. Open one to see the items, who counted each line, and the full trail of who
        submitted, approved and posted it.
      </p>

      {!!stillPosting.length && (
        <div className="border border-[#B2DDFF] bg-[#EFF8FF] rounded-xl px-4 py-3 flex flex-wrap items-center gap-3"
          data-testid="pi-audit-still-posting-banner">
          <div className="flex-1 min-w-[260px]">
            <p className="text-xs font-bold text-[#175CD3]">
              {stillPosting.length} count(s) still show &quot;Posting to SAP&quot;
            </p>
            <p className="text-[11px] text-[#175CD3]">
              That only happens when SAP&apos;s answer never came back. One check asks SAP about all of them and settles
              each - you never have to open them one by one.
            </p>
          </div>
          <Button size="sm" onClick={checkWithSap} disabled={checking} className="bg-[#175CD3] hover:bg-[#1849A9]"
            data-testid="pi-audit-check-sap-btn">
            {checking ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <ArrowsClockwise size={14} className="mr-1.5" />}
            Check movement status in SAP
          </Button>
        </div>
      )}

      {checkResult && (
        <div className="border border-[#E4E7EC] bg-white rounded-xl px-4 py-3 space-y-1" data-testid="pi-audit-check-result">
          {!!checkResult.recovered.length && (
            <p className="text-xs text-[#027A48]" data-testid="pi-audit-check-recovered">
              <CheckCircle size={13} weight="fill" className="inline mr-1.5" />
              {checkResult.recovered.length} movement(s) were in SAP all along - document(s){" "}
              <span className="font-mono font-bold">{checkResult.recovered.map((r) => r.sap_gac_id).join(", ")}</span>.
              Those counts are now marked posted.
            </p>
          )}
          {!!checkResult.not_in_sap.length && (
            <p className="text-xs text-[#B54708]" data-testid="pi-audit-check-not-in-sap">
              <WarningCircle size={13} weight="fill" className="inline mr-1.5" />
              {checkResult.not_in_sap.length} movement(s) are NOT in SAP (checked confirmations {checkResult.scanned_range}),
              so nothing was posted for them - they now read as failed and can be approved and posted again.
            </p>
          )}
          {!!(checkResult.undecided || []).length && (
            <p className="text-xs text-[#475467]" data-testid="pi-audit-check-undecided">
              {checkResult.undecided.length} movement(s) could not be decided yet: {checkResult.undecided[0].reason}
            </p>
          )}
          {!checkResult.recovered.length && !checkResult.not_in_sap.length && !(checkResult.undecided || []).length && !checkResult.error && (
            <p className="text-xs text-[#475467]" data-testid="pi-audit-check-clean">
              Nothing was left hanging - every movement already matches SAP.
            </p>
          )}
          {checkResult.error && (
            <p className="text-xs text-[#B42318]" data-testid="pi-audit-check-error">{checkResult.error}</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-28">
          <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Site</Label>
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger className="bg-white h-9" data-testid="pi-audit-site-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {sites.map((s) => <SelectItem key={s} value={s} data-testid={`pi-audit-site-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="bg-white h-9" data-testid="pi-audit-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS).map(([key, s]) => (
                <SelectItem key={key} value={key} data-testid={`pi-audit-status-${key}`}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Search</Label>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} className="h-9"
            placeholder="Count number, person or SAP document" data-testid="pi-audit-search" />
        </div>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="pi-audit-export-btn">
          <DownloadSimple size={14} className="mr-1.5" /> Export CSV
        </Button>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="pi-audit-refresh-btn">
          {loading ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <ArrowsClockwise size={14} className="mr-1.5" />} Refresh
        </Button>
      </div>

      <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-x-auto">
        <Table className="min-w-[960px]">
          <TableHeader>
            <TableRow className="bg-[#F9FAFB]">
              <TableHead className="text-xs">Count</TableHead>
              <TableHead className="text-xs">Counted on</TableHead>
              <TableHead className="text-xs">Site</TableHead>
              <TableHead className="text-xs">Type</TableHead>
              <TableHead className="text-xs">Requested by</TableHead>
              <TableHead className="text-xs text-right">Items</TableHead>
              <TableHead className="text-xs text-right">Issue qty</TableHead>
              <TableHead className="text-xs text-right">Return qty</TableHead>
              <TableHead className="text-xs">SAP document</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={11} className="text-sm text-[#667085] py-6 text-center" data-testid="pi-audit-loading">Loading...</TableCell></TableRow>}
            {!loading && !rows.length && (
              <TableRow><TableCell colSpan={11} className="text-sm text-[#667085] py-6 text-center" data-testid="pi-audit-empty">
                No physical counts match these filters.
              </TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.request_id} data-testid={`pi-audit-row-${r.request_id}`}>
                <TableCell className="font-mono text-xs font-bold text-[#101828] whitespace-nowrap">{r.request_id}</TableCell>
                <TableCell className="text-xs text-[#667085] whitespace-nowrap">{when(r.created_at)}</TableCell>
                <TableCell className="text-xs text-[#344054]">{r.site_id}</TableCell>
                <TableCell className="text-xs text-[#344054]">{CATEGORY_LABEL[r.category] || r.category}</TableCell>
                <TableCell className="text-xs text-[#344054]">{r.requester_name}</TableCell>
                <TableCell className="text-xs text-right tabular-nums">{r.summary?.total_items ?? 0}</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-[#B42318]">{fmt(r.summary?.total_issue_qty)}</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-[#027A48]">{fmt(r.summary?.total_return_qty)}</TableCell>
                <TableCell className="text-xs font-mono text-[#027A48]" data-testid={`pi-audit-docs-${r.request_id}`}>
                  {docsOf(r).join(", ") || "—"}
                </TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openDetail(r.request_id)} data-testid={`pi-audit-view-${r.request_id}`}>
                    <Eye size={12} className="mr-1" /> View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) { setDetail(null); setTrail([]); } }}>
        <DialogContent className="sm:max-w-[900px] w-[calc(100vw-1.5rem)] max-h-[88vh] overflow-y-auto" data-testid="pi-audit-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <span className="font-mono">{detail?.request_id}</span>
              {detail && <StatusBadge status={detail.status} />}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 min-w-0">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1.5 text-xs min-w-0">
                <span className="text-[#667085]">Site</span><span className="font-bold text-[#101828]">{detail.site_id}</span>
                <span className="text-[#667085]">Cost Center</span><span className="font-bold text-[#101828] font-mono">{detail.cost_center_id}</span>
                <span className="text-[#667085]">Type</span><span className="font-bold text-[#101828]">{CATEGORY_LABEL[detail.category] || detail.category}</span>
                <span className="text-[#667085]">Requested by</span><span className="font-bold text-[#101828]">{detail.requester_name}</span>
                <span className="text-[#667085]">Counted on</span><span className="font-bold text-[#101828]">{when(detail.created_at)}</span>
                <span className="text-[#667085]">Stock snapshot</span><span className="font-bold text-[#101828]">{when(detail.stock_snapshot_at)}</span>
                <span className="text-[#667085]">Decided by</span><span className="font-bold text-[#101828]">{detail.approved_by || "—"}</span>
                <span className="text-[#667085]">Decided on</span><span className="font-bold text-[#101828]">{when(detail.approved_at)}</span>
                <span className="text-[#667085]">Goods Issue qty</span><span className="font-bold text-[#B42318]">{fmt(detail.summary?.total_issue_qty)}</span>
                <span className="text-[#667085]">Goods Return qty</span><span className="font-bold text-[#027A48]">{fmt(detail.summary?.total_return_qty)}</span>
                <span className="text-[#667085]">Remarks</span><span className="font-bold text-[#101828]">{detail.remarks || "—"}</span>
                <span className="text-[#667085]">Rejection reason</span><span className="font-bold text-[#101828]">{detail.rejection_reason || "—"}</span>
              </div>

              {!!detail.postings?.length && (
                <div className="border border-[#E4E7EC] rounded-lg divide-y divide-[#F2F4F7]" data-testid="pi-audit-postings">
                  {detail.postings.map((p) => (
                    <div key={p.group} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs">
                      <span className="font-bold text-[#101828]">{p.group === "issue" ? "Goods Issue" : "Goods Return"} · {p.line_count} line(s) · {fmt(p.quantity)}</span>
                      <span className="flex items-center gap-2">
                        {p.sap_gac_id && <span className="font-mono text-[#027A48]">SAP doc {p.sap_gac_id}</span>}
                        {p.error && <span className="text-[#B42318]">{p.error}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <p className="flex items-center gap-1.5 text-xs font-bold text-[#344054] mb-2">
                  <Clock size={14} weight="fill" className="text-[#0E7C86]" /> Trail
                </p>
                <CountTrail trail={trail} loading={trailLoading} />
              </div>

              <CountLinesTable lines={detail.lines || []} testPrefix="pi-audit-line" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetail(null); setTrail([]); }} data-testid="pi-audit-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
